import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/api';

interface PaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  isDefault: boolean;
  createdAt: string;
}

interface PaymentMethodsListProps {
  clientId: string;
  onAddNew: () => void;
  stripeEnabled: boolean;
}

const getPaymentMethodLabel = (method: PaymentMethod): string => {
  if (method.type === 'us_bank_account') {
    return method.cardBrand || 'Bank Account';
  }
  switch (method.cardBrand?.toLowerCase()) {
    case 'visa': return 'Visa';
    case 'mastercard': return 'Mastercard';
    case 'amex': return 'Amex';
    case 'discover': return 'Discover';
    default: return 'Card';
  }
};

const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
  clientId,
  onAddNew,
  stripeEnabled,
}) => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);

  // Fetch payment methods
  const {
    data: paymentMethods = [],
    isLoading,
    error,
  } = useQuery<PaymentMethod[]>(
    ['paymentMethods', clientId],
    async () => {
      const response = await api.get(`/clients/${clientId}/payment-methods`);
      return response.data;
    },
    {
      enabled: !!clientId && stripeEnabled,
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (paymentMethodId: string) => {
      await api.delete(`/clients/${clientId}/payment-methods/${paymentMethodId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentMethods', clientId]);
        setDeleteDialogOpen(false);
        setMethodToDelete(null);
      },
    }
  );

  // Set default mutation
  const setDefaultMutation = useMutation(
    async (paymentMethodId: string) => {
      await api.put(`/clients/${clientId}/payment-methods/${paymentMethodId}/default`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentMethods', clientId]);
      },
    }
  );

  const handleDeleteClick = (method: PaymentMethod) => {
    setMethodToDelete(method);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (methodToDelete) {
      deleteMutation.mutate(methodToDelete.id);
    }
  };

  const handleSetDefault = (methodId: string) => {
    setDefaultMutation.mutate(methodId);
  };

  if (!stripeEnabled) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">
          Set up Stripe in Settings to manage payment methods
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Failed to load payment methods
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Saved Payment Methods
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddNew}
          variant="outlined"
        >
          Add Payment Method
        </Button>
      </Box>

      {paymentMethods.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          <CreditCardIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
          <Typography variant="body2">
            No payment methods saved
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add a card or bank account to enable faster checkout
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {paymentMethods.map((method, index) => (
            <ListItem
              key={method.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: index < paymentMethods.length - 1 ? 1 : 0,
                bgcolor: method.isDefault ? 'action.selected' : 'transparent',
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {method.type === 'us_bank_account'
                      ? <BankIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      : <CreditCardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    }
                    <Typography variant="body1">
                      {getPaymentMethodLabel(method)} •••• {method.cardLast4}
                    </Typography>
                    {method.isDefault && (
                      <Chip label="Default" size="small" color="primary" />
                    )}
                  </Box>
                }
                secondary={
                  method.type === 'us_bank_account'
                    ? 'Bank Account (ACH)'
                    : method.cardExpMonth && method.cardExpYear
                      ? `Expires ${method.cardExpMonth.toString().padStart(2, '0')}/${method.cardExpYear}`
                      : undefined
                }
              />
              <ListItemSecondaryAction>
                {!method.isDefault && (
                  <Tooltip title="Set as default">
                    <IconButton
                      size="small"
                      onClick={() => handleSetDefault(method.id)}
                      disabled={setDefaultMutation.isLoading}
                    >
                      <StarBorderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {method.isDefault && (
                  <Tooltip title="Default payment method">
                    <IconButton size="small" disabled>
                      <StarIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Remove payment method">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(method)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Payment Method</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this{' '}
            {methodToDelete?.type === 'us_bank_account' ? 'bank account' : 'card'} ending in{' '}
            <strong>•••• {methodToDelete?.cardLast4}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodsList;
